import { ComparisonGraphParams } from '../domain/comparisonGraph';
import { DataChainStoreKiParams } from '../domain/dataChainStoreKi';
import { DataChainStoreShuParams } from '../domain/dataChainStoreShu';
import { DataChainStoreShuGraphParams } from '../domain/dataChainStoreShuGraph';
import { DataKadoGraphParams } from '../domain/dataKadoGraph';
import { ApiValidationError } from '../domain/error';
import {
  DataKiSyokenKasidamaSearchParams,
  DataKiSyokenSearchParams,
} from '../domain/kiSyoken/types';

import { SIS_CUSTOM_SEARCH_DUMMY_DATE } from './dkSis';

/**
 * ================================================
 * 汎用バリデーション関数
 * それぞれのバリデーションは各画面毎に定義をします
 * 参考： domain/chain/validation.ts
 * ================================================
 */

/**
 * バリデーション可能なパラメータ型のUnion
 */
type ValidatableParams =
  | DataChainStoreKiParams
  | DataChainStoreShuParams
  | DataChainStoreShuGraphParams
  | ComparisonGraphParams
  | DataKadoGraphParams
  | DataKiSyokenSearchParams
  | DataKiSyokenKasidamaSearchParams;

/**
 * ================================================
 * ヘルパー関数（内部使用）
 * ================================================
 */

/**
 * パラメータに比較期間フィールドがあり、値が設定されているかチェック
 */
const hasComparisonDateFields = (params: ValidatableParams): boolean => {
  return (
    'startComparisonDate' in params &&
    'endComparisonDate' in params &&
    !!params.startComparisonDate &&
    !!params.endComparisonDate
  );
};

/**
 * パラメータにymdListフィールドがあり、値が設定されているかチェック
 */
const hasYmdListFields = (params: ValidatableParams): boolean => {
  const hasYmdList =
    'ymdList' in params && params.ymdList && params.ymdList.length > 0;
  const hasYmdComparisonList =
    'ymdComparisonList' in params &&
    params.ymdComparisonList &&
    params.ymdComparisonList.length > 0;

  return !!(hasYmdList || hasYmdComparisonList);
};

/**
 * パラメータにstartDate/endDateフィールドがあり、値が設定されているかチェック
 */
const hasStartEndDateFields = (params: ValidatableParams): boolean => {
  return !!(params.startDate && params.endDate);
};

/**
 * パラメータに比較期間フィールドがあり、値が設定されているかチェック（部分的でもOK）
 */
const hasAnyComparisonDateFields = (params: ValidatableParams): boolean => {
  return (
    ('startComparisonDate' in params && !!params.startComparisonDate) ||
    ('endComparisonDate' in params && !!params.endComparisonDate)
  );
};

/**
 * ================================================
 * エクスポート関数
 * ================================================
 */

/**
 * 通常の日付フィールド（startDate/endDate）が存在する場合に、比較期間の必須チェックを行う汎用関数
 *
 * @param params チェック対象のパラメータオブジェクト
 */
export const validateDateComparisonRequired = (
  params: ValidatableParams
): void => {
  if (hasStartEndDateFields(params) && !hasComparisonDateFields(params)) {
    throw new ApiValidationError(
      'startDate/endDateが指定されている場合、startComparisonDate/endComparisonDateも必要です',
      'comparisonDateRequired',
      'MISSING_COMPARISON_DATE'
    );
  }
};

/**
 * DK-SISのパラメータがカスタム検索タイプの場合に、sisStartDate/sisEndDateがSIS_CUSTOM_SEARCH_DUMMY_DATEであることを確認する関数
 *
 * カスタム検索タイプ（範囲選択・自由選択）の場合、enableSisでSIS_CUSTOM_SEARCH_DUMMY_DATEが設定される。
 * この値はAPIでハイフンを返すためのダミー値であり、上書きされてはいけない。
 *
 * @param params チェック対象のパラメータオブジェクト
 * @param dateUnit 検索タイプ（ReportsDateUnit）
 */
export const validateSisDateForCustomSearch = (
  params: ValidatableParams & {
    sisDateRangeType?: string;
    sisStartDate?: string;
    sisEndDate?: string;
  },
  dateUnit?: string
): void => {
  // sisFieldTypesが設定されている場合のみチェック
  // 空配列は「SISフィールドが選択されていない」状態を意味するため、バリデーションをスキップする
  if (
    !('sisFieldTypes' in params) ||
    (Array.isArray(params.sisFieldTypes) &&
      params.sisFieldTypes.length === 0) ||
    !params.sisFieldTypes
  ) {
    return;
  }

  // カスタム検索タイプかどうかを判定
  // dateUnitが'範囲選択'または'自由選択'または'カスタム'の場合、または
  // sisStartDate/sisEndDateがSIS_CUSTOM_SEARCH_DUMMY_DATEの場合（sisDateRangeTypeの値は関係ない）
  const isCustomSearchType =
    dateUnit === '範囲選択' ||
    dateUnit === '自由選択' ||
    dateUnit === 'カスタム' ||
    params.sisStartDate === SIS_CUSTOM_SEARCH_DUMMY_DATE ||
    params.sisEndDate === SIS_CUSTOM_SEARCH_DUMMY_DATE;

  if (isCustomSearchType) {
    // カスタム検索タイプの場合、sisStartDate/sisEndDateはSIS_CUSTOM_SEARCH_DUMMY_DATEである必要がある
    // sisFieldTypesが設定されている場合、これらの日付は必須であるため、undefinedの場合もエラーとする
    if (params.sisStartDate !== SIS_CUSTOM_SEARCH_DUMMY_DATE) {
      throw new ApiValidationError(
        'DK-SISの日付が不正です。',
        'invalidSisStartDateForCustomSearch',
        'INVALID_SIS_START_DATE_FOR_CUSTOM_SEARCH'
      );
    }
    if (params.sisEndDate !== SIS_CUSTOM_SEARCH_DUMMY_DATE) {
      throw new ApiValidationError(
        'DK-SISの日付が不正です。',
        'invalidSisEndDateForCustomSearch',
        'INVALID_SIS_END_DATE_FOR_CUSTOM_SEARCH'
      );
    }
  }
};

/**
 * ymdList/ymdComparisonListとstartDate/endDate系の日付指定が重複していないかをチェックする汎用関数
 *
 * @param params チェック対象のパラメータオブジェクト
 */
export const validateYmdListAndStartEndNotDuplicated = (
  params: ValidatableParams
): void => {
  const hasYmdFields = hasYmdListFields(params);
  const hasDateFields =
    hasStartEndDateFields(params) || hasAnyComparisonDateFields(params);

  if (hasYmdFields && hasDateFields) {
    throw new ApiValidationError(
      'ymdList/ymdComparisonListとstartDate/endDate系の日付指定は同時に使用できません',
      'duplicatedDateSpecification',
      'DUPLICATED_DATE_SPECIFICATION'
    );
  }
};
